<template>
    <div class="max-w-6xl mx-auto pb-6 space-y-6">
        <h1 class="text-2xl font-bold text-gray-800">{{ isEdit ? 'Atualizar Paciente' : 'Novo Paciente' }}</h1>

        <Loading :overlay-text="overlayText || 'Buscando informações'" :overlay="true" :show="loading" />

        <form v-if="!loading" @submit.prevent="submit" novalidate class="space-y-6">
            <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 class="text-base font-semibold text-gray-500 mb-4">Foto</h2>

                <div class="flex flex-col items-center gap-y-2">
                    <label
                        for="photoUpload"
                        class="relative inline-block rounded-full group"
                        :class="uploadingPhoto ? 'cursor-wait' : 'cursor-pointer'"
                    >
                        <ThumbRounded size="large" :path="patient.photo_url" :name="patient.name" />
                        <div
                            class="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 transition-colors"
                            :class="uploadingPhoto ? 'bg-black/40' : 'group-hover:bg-black/40'"
                        >
                            <svg v-if="uploadingPhoto" class="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <span
                            v-if="!uploadingPhoto"
                            class="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-cyan-600 text-white ring-2 ring-white shadow-sm transition-transform group-hover:scale-110"
                        >
                            <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16.5A2.5 2.5 0 016.5 14H7a5 5 0 019.7-1.7A3.5 3.5 0 0118 19H6.5A2.5 2.5 0 014 16.5Z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9.5 12.5a2.5 2.5 0 105 0 2.5 2.5 0 00-5 0z" />
                            </svg>
                        </span>
                        <input
                            id="photoUpload"
                            type="file"
                            class="hidden"
                            :accept="acceptMimeTypeImage"
                            :disabled="uploadingPhoto"
                            @change="onPhotoFileChange"
                        />
                    </label>
                    <span class="text-xs text-gray-400">{{ uploadingPhoto ? 'Enviando...' : (patient.photo_url ? 'Clique para alterar a foto' : 'Clique para adicionar uma foto') }}</span>
                </div>
            </section>

            <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 class="text-base font-semibold text-gray-500 mb-4">Dados Pessoais</h2>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-x-4">
                    <div>
                        <BaseInput :is-invalid="v$.name.$error" label="Nome" @blur="v$.name.$touch" v-model="patient.name" :required="true" />
                        <div v-for="error in v$.$errors" :key="error.$uid">
                            <BaseErrorMessage v-if="error.$property === 'name'" :message="error.$message" />
                        </div>
                    </div>
                    <div>
                        <BaseInput label="CPF/CNPJ" inputmode="numeric" v-maska="MaskService['document']" v-model="patient.document" />
                    </div>
                    <div>
                        <BaseInput label="Telefone" inputmode="numeric" v-maska="MaskService['phone']" v-model="patient.phone" />
                    </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-x-4 mt-4">
                    <div>
                        <BaseInput label="Email" v-model="patient.email" />
                    </div>
                    <div>
                        <BaseInput label="Data de Nascimento" inputmode="numeric" v-maska="MaskService['date']" v-model="patient.birthdate" />
                    </div>
                    <div>
                        <BaseInput label="Profissão" v-model="patient.profession" />
                    </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-x-4 mt-4">
                    <div>
                        <BaseSelect label="Sexo" :options="genderOptions" v-model="patient.gender"/>
                    </div>
                    <div>
                        <BaseInput label="Médico" v-model="patient.doctor" />
                    </div>
                </div>
            </section>

            <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 class="text-base font-semibold text-gray-500 mb-4">Endereço</h2>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-x-4">
                    <div>
                        <div class="flex gap-x-2">
                            <label class="label">CEP</label>
                            <svg v-if="loadingZipcode" class="animate-spin h-5 w-5 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <BaseInput inputmode="numeric" v-maska="MaskService['cep']" v-model="patient.zipcode" @blur="searchCep(patient.zipcode)" />
                    </div>
                    <div>
                        <BaseSelect :disabled="loadingZipcode" label="Estado" :options="stateOptions" v-model.number="patient.state" @change="onSelectState()"/>
                    </div>
                    <div>
                        <BaseSelect :label="loadingCities ? 'Buscando cidades...' : 'Cidade' " :options="cityOptions" :disabled="!patient.state || loadingCities || loadingZipcode" v-model.number="patient.city"/>
                    </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-x-4 mt-4">
                    <div class="sm:col-span-3">
                        <BaseInput :disabled="loadingZipcode" label="Endereço" v-model="patient.address" />
                    </div>
                </div>
            </section>

            <section class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 class="text-base font-semibold text-gray-500 mb-4">Anamnese</h2>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-x-4">
                    <div class="sm:col-span-2">
                        <label class="label">Modalidade</label>
                        <v-select multiple :closeOnSelect="false" v-model="patient.modality" class="multi-select" :options="modalityOptions"></v-select>
                    </div>
                    <div>
                        <BaseSelect label="Nível" :options="levelOptions" v-model="patient.level"/>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-x-4 mt-4">
                    <div>
                        <BaseTextarea label="Queixa Principal" v-model="patient.chief_complaint"></BaseTextarea>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-x-4 mt-4">
                    <div class="col-span-3">
                        <BaseTextarea label="HDA" v-model="patient.history_current_illness"></BaseTextarea>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-x-4 mt-4">
                    <div class="col-span-3">
                        <BaseTextarea label="História Pregressa" v-model="patient.history_past_illness"></BaseTextarea>
                    </div>
                </div>

                <div class="mt-4">
                    <label class="label">Exames Complementares</label>
                    <input ref="fileInput" type="file" multiple class="hidden" @change="onFileInputChange" />
                    <div
                        class="mt-2 w-full cursor-pointer rounded-[24px] border border-dashed px-6 py-8 transition-all"
                        :class="dragActive ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200 bg-slate-50/80 hover:border-cyan-300 hover:bg-cyan-50/60'"
                        @click="openFileDialog"
                        @dragenter.prevent="dragActive = true"
                        @dragover.prevent="dragActive = true"
                        @dragleave.prevent="handleDragLeave"
                        @drop.prevent="handleFileDrop"
                    >
                        <div class="flex flex-col items-center justify-center text-center">
                            <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-cyan-600 shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 16V8m0 0l-3 3m3-3l3 3M4 16.5A2.5 2.5 0 016.5 14H7a5 5 0 019.7-1.7A3.5 3.5 0 0118 19H6.5A2.5 2.5 0 014 16.5Z" />
                                </svg>
                            </div>
                            <div class="mt-4 text-base font-semibold text-slate-700">Arraste arquivos aqui ou clique para enviar</div>
                            <div class="mt-2 max-w-2xl text-sm leading-6 text-slate-400">Os arquivos enviados aparecem logo abaixo e podem ser removidos com confirmação.</div>
                            <div v-if="uploadingFiles" class="mt-5 w-full max-w-xl">
                                <div class="h-2 overflow-hidden rounded-full bg-cyan-100">
                                    <div class="h-full rounded-full bg-cyan-500 transition-all" :style="{ width: `${uploadProgress}%` }"></div>
                                </div>
                                <div class="mt-2 text-xs font-semibold text-cyan-700">Enviando {{ uploadProgress }}%</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1">
                    <FileTable class="mt-4" :loading="loadingFiles" :files="patient.files" @removeFile="removeFile" />
                </div>

                <div class="grid grid-cols-3 gap-x-4 mt-4">
                    <div class="col-span-3">
                        <BaseTextarea label="Fatores Psicossociais" v-model="patient.psychosocial_factors"></BaseTextarea>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-x-4 mt-4">
                    <div class="col-span-3">
                        <BaseTextarea label="Doenças Préexistentes" v-model="patient.pre_existing_disease"></BaseTextarea>
                    </div>
                </div>

                <div class="grid grid-cols-3 gap-x-4 mt-4">
                    <div class="col-span-3">
                        <BaseTextarea label="Objetivos e Contextos" v-model="patient.context"></BaseTextarea>
                    </div>
                </div>
            </section>

            <div class="flex flex-col">
                <div class="flex justify-end gap-x-4">
                    <BackButton></BackButton>
                    <BaseButton skin="primary" :is-submit="true">{{ isEdit ? "Atualizar" : "Salvar" }}</BaseButton>
                </div>
                <BaseErrorMessage class="mt-2" v-if="formErrorMessage" message="Existem campos obrigatórios que não foram preenchidos, volte e preencha todos os campos obrigatórios." />
            </div>
        </form>
    </div>
</template>

<style>
    .multi-select .vs__search::placeholder,
    .multi-select .vs__dropdown-toggle,
    .multi-select .vs__dropdown-menu {
        padding: 8px;
    }
</style>

<script>


import { ref, onMounted, computed } from 'vue';
import { defaultDate } from '../filters/date';
import { useRoute, useRouter } from 'vue-router';
import FileTable from '@/components/FileTable';
import Swal from 'sweetalert';
import MaskService from '../services/MaskService';
import { requiredMessage } from '../services/VuelidateMessageService';
import { useVuelidate } from '@vuelidate/core';
import ThumbRounded from '@/components/ThumbRounded';
import { GeoInfoService, PatientService, MetaService } from '../services/Entity';
import UploadService from '../services/FileService';

export default {
    name: 'PatientForm',
    components: {
        ThumbRounded,
        FileTable,
    },
    setup() {
        const acceptMimeTypeImage = 'image/png,image/gif,image/jpeg,image/webp';
        const MAX_PHOTO_SIZE = process.env.VUE_APP_MAX_SIZE_PHOTO_BYTES;
        const uploadingPhoto = ref(false);
        const isEdit = ref(false);
        const patient = ref({
            files: [],
            photo_url: ''
        });
        const route = useRoute();
        const router = useRouter();
        const loading = ref(false);
        const loadingCities = ref(false);
        const loadingFiles = ref(false);
        const uploadingFiles = ref(false);
        const uploadProgress = ref(0);
        const dragActive = ref(false);
        const fileInput = ref(null);
        const stateOptions = ref([]);
        const cityOptions = ref([]);
        const modalityOptions = ref([]);
        const loadingZipcode = ref(false);
        const formErrorMessage = ref(false);
        const genderOptions = ref([
            { title: 'Masculino', value: 'male' },
            { title: 'Feminino', value: 'female' },
        ]);
        const levelOptions = ref([
            { title: 'Alto Rendimento Profissional', value: 'high_professional' },
            { title: 'Alto Rendimento Recreacional', value: 'high_fun' },
            { title: 'Atleta de fim de semana', value: 'casual' },
        ]);
        const patientRules = computed(() => ({
            name: { required: requiredMessage },
        }));
        const chiefComplaintTypeOptions = ref([
            "Muscular",
            "Articular",
            "Tendão",
            "Ligamento",
            "Visceral",
            "Radicular",
            "Ósseo",
            "Outros"
        ]);

        onMounted(async () => {
            const patientId = handleRoute(route);
            loading.value = true;

            await listModalities();

            if (patientId) {
                isEdit.value = true;
                await listStates();
                await getPatient(patientId);

                if (patient.value.state && patient.value.state.id) {
                    patient.value.state = patient.value.state.id;

                    if (patient.value.city) {
                        patient.value.city = patient.value.city.id;
                    }

                    await listCities(patient.value.state);
                }

                return;
            }

            await listStates();

            loading.value = false;
        });

        async function submit() {
            const isValid = await v$.value.$validate();

            if (!isValid) {
                formErrorMessage.value = true;
                return;
            }

            formErrorMessage.value = false;

            if (!isEdit.value) {
                await save();
            } else {
                await update();
            }
        }

        async function listModalities() {
            try {
                const { data } = await MetaService.listModalities();

                modalityOptions.value = data.map(modality => {
                    return {
                        label: modality.name,
                        id: modality.id
                    }
                });
            } catch(err) {
                loading.value = false;
                console.log(err);
                Swal({
                    title: 'Ocorreu um problema ao listar os estados',
                    icon: 'error'
                });
            }
        }

        async function listStates() {
            try {
                const { data } = await GeoInfoService.listStates();

                stateOptions.value = data.map(state => {
                    return {
                        title: state.name,
                        value: state.id
                    }
                });
            } catch(err) {
                console.log(err);
                Swal({
                    title: 'Ocorreu um problema ao listar os estados',
                    icon: 'error'
                });
            }
        }

        async function onSelectState() {
            const selectedState = patient.value.state;
            await listCities(selectedState);
        }

        async function listCities(stateId) {
            try {
                if (!stateId) return;
                loadingCities.value = true;

                const { data } = await GeoInfoService.listCities(stateId);

                cityOptions.value = data.map(city => {
                     return {
                         title: city.name,
                         value: city.id
                     }
                 });

                 loadingCities.value = false;
            } catch(err) {
                console.log(err);
                loadingCities.value = false;
                Swal({
                    title: 'Ocorreu um problema ao listar os estados',
                    icon: 'error'
                });
            }
        }

        async function save() {
            try {
                loading.value = true;
                const { data } = await PatientService.create(patient.value);

                Swal({
                    title: 'Paciente cadastrado!',
                    icon: 'success'
                });

                router.push({ name: 'records', params: { uuid: data.uuid } });

                loading.value = false;
            } catch(err) {
                loading.value = false;

                if (err.data && err.data.error == 'already_exists') {
                    Swal({
                        title: 'Já existe um paciente com este mesmo CPF',
                        icon: 'info'
                    });
                    return;
                }

                Swal({
                    title: 'Ocorreu um problema ao salvar o paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        async function update() {
            try {
                loading.value = true;
                const { data } = await PatientService.update(patient.value);

                Swal({
                    title: 'Paciente Atualizado!',
                    icon: 'success'
                });

                router.push({ name: 'records', params: { uuid: data.uuid } });

                loading.value = false;
            } catch(err) {
                loading.value = false;

                if (err.data && err.data.error == 'already_exists') {
                    Swal({
                        title: 'Já existe uma meta para esta área de negócio no mesmo período',
                        icon: 'info'
                    });
                    return;
                }

                Swal({
                    title: 'Ocorreu um problema ao atualizar o paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        function onUpload(data) {
            const { success } = data;
            const file = success[0];
            patient.value.files.push(file);
        }

        function onUploadPhoto(data) {
            const { success } = data;
            const file = success[0];

            patient.value.photo_url = file.url;
            patient.value.photo = file.id;
        }

        async function onPhotoFileChange(event) {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) return;

            if (file.size > MAX_PHOTO_SIZE) {
                Swal({
                    title: 'Arquivo muito grande',
                    text: 'O limite máximo para imagens é 50mb',
                    icon: 'warning'
                });
                return;
            }

            try {
                uploadingPhoto.value = true;
                const response = await UploadService.upload(file, { patientUuid: patient.value.uuid || null, folderType: 'arquivos' });

                onUploadPhoto(response.data);
            } catch(err) {
                console.log(err);
                Swal({
                    title: 'Erro ao enviar foto',
                    text: 'Tente novamente, caso o erro persista verifique se o arquivo está corrompido.',
                    icon: 'error'
                });
            } finally {
                uploadingPhoto.value = false;
            }
        }

        async function removeFile(fileId) {
            try {
                Swal({
                title: 'Deseja realmente remover o arquivo?',
                text: 'Não será possível restaurar o mesmo',
                icon: 'warning',
                buttons: {
                    cancel: 'Cancelar',
                    confirm: {
                        text: 'Deletar',
                        className: 'swal-button--danger'
                    }
                }}).then(async (confirm) => {
                    if (!confirm) return;

                    const requestData = { patient: patient.value.id, file: fileId };

                    loadingFiles.value = true;
                    patient.value.files.forEach((file, index) => {
                        if (file.id == fileId) {
                            patient.value.files.splice(index, 1);
                        }
                    });

                    if (!isEdit.value) {
                        loadingFiles.value = false;
                        return;
                    }

                    await PatientService.removeFile(requestData.patient, requestData.file, requestData);
                    loadingFiles.value = false;
                });
            } catch(err) {
                loadingFiles.value = false;
                console.log(err);
                Swal({
                    title: 'Ocorreu um erro ao excluir o arquivo',
                    text: 'Atualize a página e tente novamente',
                    icon: 'error'
                });
            }
        }

        function openFileDialog() {
            if (uploadingFiles.value) return;
            fileInput.value?.click();
        }

        function onFileInputChange(event) {
            const files = event.target.files;
            if (!files || !files.length) return;
            uploadFiles(files);
            event.target.value = '';
        }

        function handleDragLeave(event) {
            if (event.currentTarget && event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
                return;
            }
            dragActive.value = false;
        }

        function handleFileDrop(event) {
            dragActive.value = false;
            const files = event.dataTransfer?.files;
            if (!files || !files.length) return;
            uploadFiles(files);
        }

        async function uploadFiles(fileList) {
            const files = Array.from(fileList || []);
            if (!files.length) return;

            const failedFiles = [];
            uploadingFiles.value = true;
            loadingFiles.value = true;
            uploadProgress.value = 0;

            for (let index = 0; index < files.length; index++) {
                const file = files[index];

                try {
                    const response = await UploadService.upload(
                        file,
                        { patientUuid: patient.value.uuid || null, folderType: 'arquivos' },
                        (event) => {
                            const fileProgress = event.total ? event.loaded / event.total : 0;
                            uploadProgress.value = Math.round(((index + fileProgress) / files.length) * 100);
                        }
                    );

                    onUpload(response.data);
                } catch (err) {
                    failedFiles.push(file.name);
                }
            }

            uploadingFiles.value = false;
            loadingFiles.value = false;
            uploadProgress.value = 0;

            if (failedFiles.length) {
                Swal({
                    title: 'Alguns arquivos não foram enviados',
                    text: failedFiles.join(', '),
                    icon: 'warning'
                });
            }
        }

        function handleRoute(route) {
            const patientUUID = route.params.uuid;

            return patientUUID ? patientUUID : false;
        }

        async function getPatient(patientUUID) {
            try {
                loading.value = true;
                let { data } = await PatientService.getByUUID(patientUUID);

                if (data.birthdate) {
                    data.birthdate = defaultDate(data.birthdate);
                }

                patient.value = data;
                loading.value = false;
            } catch(err) {
                loading.value = false;
                console.log(err);

                Swal({
                    title: 'Ocorreu um problema ao consultar os dados do paciente',
                    text: 'Atualize a página e tente novamente, se o erro persistir entre em contato com o suporte',
                    icon: 'error'
                });
            }
        }

        async function searchCep(zipcode) {
            try {
                loadingZipcode.value = true;
                let { data } = await GeoInfoService.searchZipCode(zipcode);

                if (data.state) {
                    await listCities(data.state);

                    patient.value.state = data.state;
                    patient.value.city = data.city;
                }

                patient.value.address = `${data.street} - ${data.neighborhood}`;

                loadingZipcode.value = false;
            } catch(err) {
                loadingZipcode.value = false;
                console.log(err);

                Swal({
                    title: 'CEP não encontrado, digite manualmente',
                    icon: 'warning'
                });
            }
        }

        const v$ = useVuelidate(patientRules, patient);

        return {
            v$,
            submit,
            isEdit,
            loading,
            searchCep,
            loadingZipcode,
            fileInput,
            dragActive,
            uploadingFiles,
            uploadProgress,
            onUpload,
            onUploadPhoto,
            onPhotoFileChange,
            uploadingPhoto,
            removeFile,
            openFileDialog,
            onFileInputChange,
            handleDragLeave,
            handleFileDrop,
            formErrorMessage,
            onSelectState,
            loadingCities,
            stateOptions,
            cityOptions,
            loadingFiles,
            modalityOptions,
            acceptMimeTypeImage,
            chiefComplaintTypeOptions,
            levelOptions,
            genderOptions,
            MaskService,
            patient
        }
    },
}
</script>
